import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer
} from 'react';

import isNotUndefinedOrNull from '@zenbusiness/application-commons-utils/isNotUndefinedOrNull';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import MoneyInputBase from '../BaseMoneyInput';
import ErrorMessageWithButton from '../ErrorMessageWithButton';
import Skeleton from '../Skeleton';

import { ERROR_REASONS } from './constants/errors';
import { formatMoneyDisplay, convertCentToDollar } from './utils/format';

const ACTION_TYPES = {
  SET_VALUE: 'SET_VALUE',
  SET_REASON: 'SET_REASON',
  SET_TOUCH: 'SET_TOUCH'
};

const EMPTY_VALUE_MESSAGE = 'Please enter an amount.';
const SMALLER_THAN_MIN_VALUE_PER_TRANSFER_MESSAGE = (minValuePerTransfer) =>
  `Deposit must be at least ${formatMoneyDisplay(convertCentToDollar(minValuePerTransfer))}`;

const EXCEED_DAILY_LIMIT_MESSAGE = (dailyLimit) =>
  `This exceeds the daily transfer limit of ${formatMoneyDisplay(convertCentToDollar(dailyLimit))}`;

const EXCEED_MONTHLY_LIMIT_MESSAGE = (monthlyLimit) =>
  `This exceeds the monthly transfer limit of ${formatMoneyDisplay(
    convertCentToDollar(monthlyLimit)
  )}`;

const EXCEED_PER_TRANSFER_LIMIT_MESSAGE = (perTransferLimit) =>
  `This exceeds the transfer limit of ${formatMoneyDisplay(convertCentToDollar(perTransferLimit))}`;

// Astra's fixed weekly transfer limit, not returned by the instant deposit limit query.
const WEEKLY_TRANSFER_LIMIT_CENTS = 500000;

// TODO: Handle input validation
const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_VALUE:
      return { ...state, value: action.payload };
    case ACTION_TYPES.SET_REASON:
      return { ...state, reason: action.payload };
    default:
      return state;
  }
};

const InstanceDepositMoneyInput = forwardRef(function MoneyInput(
  {
    title,
    defaultValue,
    dailyRemainLimit,
    dailyLimit,
    monthlyRemainLimit,
    monthlyLimit,
    perTransferLimit,
    minValuePerTransfer,
    handleDataChange,
    dailyTransferLimitRemaining,
    dailyTransferCountLimit
  },
  ref
) {
  const [state, dispatch] = useReducer(reducer, {
    value: defaultValue,
    reason: null
  });
  const { reason, value } = state;

  const errorMessage = useMemo(() => {
    switch (reason) {
      case ERROR_REASONS.EMPTY_VALUE:
        return <ErrorMessageWithButton message={EMPTY_VALUE_MESSAGE} />;
      case ERROR_REASONS.SMALLER_THAN_MIN_VALUE_PER_TRANSFER:
        return (
          <ErrorMessageWithButton
            message={SMALLER_THAN_MIN_VALUE_PER_TRANSFER_MESSAGE(minValuePerTransfer)}
          />
        );
      case ERROR_REASONS.EXCEED_DAILY_LIMIT:
        return <ErrorMessageWithButton message={EXCEED_DAILY_LIMIT_MESSAGE(dailyLimit)} />;
      case ERROR_REASONS.EXCEED_MONTHLY_LIMIT:
        return <ErrorMessageWithButton message={EXCEED_MONTHLY_LIMIT_MESSAGE(monthlyLimit)} />;
      case ERROR_REASONS.EXCEED_PER_TRANSFER_LIMIT:
        return (
          <ErrorMessageWithButton message={EXCEED_PER_TRANSFER_LIMIT_MESSAGE(perTransferLimit)} />
        );
      default:
        return null;
    }
  }, [dailyLimit, minValuePerTransfer, monthlyLimit, perTransferLimit, reason]);

  /**
   * Auto validate is used when the user types in the input
   *
   * @returns {boolean} true if the value is valid, false otherwise
   */
  const autoValidateMinMaxLimitChecks = useCallback(
    (newValue) => {
      if (newValue > 0 && newValue < minValuePerTransfer) {
        dispatch({
          type: ACTION_TYPES.SET_REASON,
          payload: ERROR_REASONS.SMALLER_THAN_MIN_VALUE_PER_TRANSFER
        });
        return false;
      }

      if (newValue > perTransferLimit) {
        dispatch({
          type: ACTION_TYPES.SET_REASON,
          payload: ERROR_REASONS.EXCEED_PER_TRANSFER_LIMIT
        });
        return false;
      }

      dispatch({ type: ACTION_TYPES.SET_REASON, payload: null });
      return true;
    },
    [minValuePerTransfer, perTransferLimit]
  );

  // Trigger auto validate at the first time user see the modal
  useEffect(() => {
    const isError = !autoValidateMinMaxLimitChecks(value);
    handleDataChange({ value, isError });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoValidateMinMaxLimitChecks]);

  /**
   * Manual validate is used when the user clicks the submit button
   *
   * @param {number} newValue the new value of the input
   * @returns {boolean} true if the value is valid, false otherwise
   */
  const manualValidateMinMaxLimitChecks = useCallback(
    (newValue) => {
      if (dailyRemainLimit < newValue) {
        dispatch({
          type: ACTION_TYPES.SET_REASON,
          payload: ERROR_REASONS.EXCEED_DAILY_LIMIT
        });
        return false;
      }

      if (monthlyRemainLimit < newValue) {
        dispatch({
          type: ACTION_TYPES.SET_REASON,
          payload: ERROR_REASONS.EXCEED_MONTHLY_LIMIT
        });
        return false;
      }

      dispatch({ type: ACTION_TYPES.SET_REASON, payload: null });
      return true;
    },
    [dailyRemainLimit, monthlyRemainLimit]
  );

  useImperativeHandle(
    ref,
    () => ({
      validate() {
        const valid = manualValidateMinMaxLimitChecks(value);
        handleDataChange({ value, isError: !valid });
        return valid;
      }
    }),
    [handleDataChange, manualValidateMinMaxLimitChecks, value]
  );

  const dailyTransferLimitText =
    dailyTransferLimitRemaining === dailyTransferCountLimit
      ? `Up to ${dailyTransferCountLimit} transactions per day`
      : `${dailyTransferLimitRemaining} daily transaction(s) remaining`;
  const limitText = `Must be at least ${formatMoneyDisplay(
    convertCentToDollar(minValuePerTransfer),
    2,
    0
  )} and up to ${formatMoneyDisplay(
    convertCentToDollar(perTransferLimit),
    2,
    0
  )}. ${dailyTransferLimitText}.`;

  const monthlyLimitText = isNotUndefinedOrNull(monthlyRemainLimit) ? (
    `Monthly limit remaining ${formatMoneyDisplay(convertCentToDollar(monthlyRemainLimit), 2, 0)}.`
  ) : (
    <Skeleton
      aria-label="Loading daily limit"
      width={300}
    />
  );

  const weeklyLimitText = `Weekly limit ${formatMoneyDisplay(
    convertCentToDollar(WEEKLY_TRANSFER_LIMIT_CENTS),
    2,
    0
  )}.`;

  const handleChange = (newValue) => {
    dispatch({ type: ACTION_TYPES.SET_VALUE, payload: newValue });
    const isError = !autoValidateMinMaxLimitChecks(newValue);
    handleDataChange({ value: newValue, isError });
  };

  return (
    <Stack gap="16px">
      <Stack gap="16px">
        <Text
          size="md"
          weight="medium"
        >
          {title}
        </Text>
        <Text size="sm">{limitText}</Text>
        <Text size="sm">{monthlyLimitText}</Text>
        <Text size="sm">{weeklyLimitText}</Text>
      </Stack>
      <MoneyInputBase
        defaultValue={defaultValue}
        error={!!errorMessage}
        onChangeValue={handleChange}
      />
      <Text
        color="tertiary"
        size="lg"
      >
        The connected card must have sufficient funds.
      </Text>
      {errorMessage}
    </Stack>
  );
});

InstanceDepositMoneyInput.propTypes = {
  title: PropTypes.string.isRequired,
  inputRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.node, PropTypes.any])
  }),
  defaultValue: PropTypes.number.isRequired,
  monthlyRemainLimit: PropTypes.number,
  dailyRemainLimit: PropTypes.number,
  perTransferLimit: PropTypes.number,
  dailyLimit: PropTypes.number,
  monthlyLimit: PropTypes.number,
  minValuePerTransfer: PropTypes.number,
  handleDataChange: PropTypes.func,
  dailyTransferLimitRemaining: PropTypes.number,
  dailyTransferCountLimit: PropTypes.number
};

export default InstanceDepositMoneyInput;
